import { useState, useEffect } from 'react';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  BookOpen, Search, Heart, Target, Users, Feather, 
  Moon, Compass, CloudRain, Sun, Bookmark, BookmarkCheck, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  Heart, Sun, Target, Users, Feather, Moon, Compass, CloudRain
};

export default function Library() {
  const [categories, setCategories] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryContent, setCategoryContent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [activeTab, setActiveTab] = useState('categories');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (searchQuery.length >= 2) {
        searchVerses(searchQuery);
      } else {
        setSearchResults(null);
      }
    }, 300);
    return () => clearTimeout(delaySearch);
  }, [searchQuery]);

  const fetchData = async () => {
    try {
      const [catRes, bookRes] = await Promise.all([
        axios.get(`${API}/library/categories`),
        axios.get(`${API}/bookmarks`)
      ]);
      setCategories(catRes.data);
      setBookmarks(bookRes.data);
    } catch (error) {
      toast.error('Could not load library');
    } finally {
      setLoading(false);
    }
  };

  const selectCategory = async (category) => {
    setSelectedCategory(category);
    try {
      const response = await axios.get(`${API}/library/category/${category.id}`);
      setCategoryContent(response.data);
    } catch (error) {
      toast.error('Could not load category');
    }
  };

  const searchVerses = async (query) => {
    if (query.length < 2) {
      setSearchResults(null);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/library/search?q=${encodeURIComponent(query)}`);
      setSearchResults(response.data.results);
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  const addBookmark = async (type, content, reference) => {
    try {
      const response = await axios.post(`${API}/bookmarks`, { type, content, reference });
      setBookmarks(prev => [response.data, ...prev]);
      toast.success('Added to bookmarks');
    } catch (error) {
      toast.error('Could not bookmark');
    }
  };

  const removeBookmark = async (bookmarkId) => {
    try {
      await axios.delete(`${API}/bookmarks/${bookmarkId}`);
      setBookmarks(prev => prev.filter(b => b.id !== bookmarkId));
      toast.success('Removed from bookmarks');
    } catch (error) {
      toast.error('Could not remove bookmark');
    }
  };

  const isBookmarked = (content) => {
    return bookmarks.some(b => b.content === content);
  };

  const filteredCategories = categories.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderSearchResults = () => {
    if (!searchResults) return null;
    
    if (searchResults.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-muted-foreground">No verses found matching &ldquo;{searchQuery}&rdquo;</p>
        </div>
      );
    }

    return (
      <div className="space-y-3 pb-24" data-testid="search-results">
        <p className="text-sm text-muted-foreground">{searchResults.length} verse(s) found</p>
        {searchResults.map((result, idx) => (
          <Card key={idx} className="rounded-2xl border-border/40">
            <CardContent className="p-4">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                {result.category}
              </span>
              <p className="text-foreground leading-relaxed my-2 italic">
                &ldquo;{result.verse}&rdquo;
              </p>
              <div className="flex items-center justify-between">
                <p className="text-sm text-primary font-medium">{result.reference}</p>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => isBookmarked(result.verse) 
                    ? removeBookmark(bookmarks.find(b => b.content === result.verse)?.id)
                    : addBookmark('verse', result.verse, result.reference)
                  }
                >
                  {isBookmarked(result.verse) ? (
                    <BookmarkCheck className="w-4 h-4 text-primary" />
                  ) : (
                    <Bookmark className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background pb-safe" data-testid="library-page">
      {/* Header */}
      <header className="px-6 pt-8 pb-4">
        <h1 className="text-2xl font-serif mb-4">Library</h1>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search verses... (e.g., peace, love, anxiety)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 rounded-full bg-muted/50 border-transparent"
            data-testid="search-input"
          />
        </div>
      </header>

      {/* Search Results */}
      {searchResults !== null && searchQuery.length >= 2 ? (
        <ScrollArea className="flex-1 px-6">
          {renderSearchResults()}
        </ScrollArea>
      ) : (
        <>
          {/* Tabs */}
          <div className="px-6 mb-4">
            <div className="flex gap-2">
              <Button
                variant={activeTab === 'categories' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-full"
                onClick={() => setActiveTab('categories')}
                data-testid="tab-categories"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Categories
          </Button>
          <Button
            variant={activeTab === 'bookmarks' ? 'default' : 'ghost'}
            size="sm"
            className="rounded-full"
            onClick={() => setActiveTab('bookmarks')}
            data-testid="tab-bookmarks"
          >
            <Bookmark className="w-4 h-4 mr-2" />
            Saved ({bookmarks.length})
          </Button>
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1 px-6">
        {activeTab === 'categories' ? (
          selectedCategory ? (
            <div className="space-y-4 pb-24" data-testid="category-content">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedCategory(null);
                  setCategoryContent(null);
                }}
                className="rounded-full"
              >
                <X className="w-4 h-4 mr-1" />
                Back
              </Button>
              
              <div className="flex items-center gap-3 mb-4">
                {(() => {
                  const Icon = ICONS[selectedCategory.icon] || BookOpen;
                  return (
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                  );
                })()}
                <div>
                  <h2 className="text-xl font-serif">{selectedCategory.name}</h2>
                  <p className="text-sm text-muted-foreground">{selectedCategory.description}</p>
                </div>
              </div>

              {categoryContent?.verses?.length > 0 ? (
                <div className="space-y-3">
                  {categoryContent.verses.map((verse, idx) => (
                    <Card key={idx} className="rounded-2xl border-border/40">
                      <CardContent className="p-4">
                        <p className="text-foreground leading-relaxed mb-2 italic">
                          &ldquo;{verse.verse}&rdquo;
                        </p>
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-primary font-medium">{verse.reference}</p>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => isBookmarked(verse.verse) 
                              ? removeBookmark(bookmarks.find(b => b.content === verse.verse)?.id)
                              : addBookmark('verse', verse.verse, verse.reference)
                            }
                          >
                            {isBookmarked(verse.verse) ? (
                              <BookmarkCheck className="w-4 h-4 text-primary" />
                            ) : (
                              <Bookmark className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No content available for this category yet.
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 pb-24" data-testid="categories-grid">
              {loading ? (
                Array.from({ length: 8 }).map((_, idx) => (
                  <Card key={idx} className="rounded-2xl animate-pulse">
                    <CardContent className="p-4 h-28 bg-muted" />
                  </Card>
                ))
              ) : (
                filteredCategories.map((category) => {
                  const Icon = ICONS[category.icon] || BookOpen;
                  return (
                    <Card
                      key={category.id}
                      className="rounded-2xl border-border/40 hover:shadow-float transition-all cursor-pointer card-hover"
                      onClick={() => selectCategory(category)}
                      data-testid={`category-${category.id}`}
                    >
                      <CardContent className="p-4">
                        <Icon className="w-8 h-8 text-primary mb-3" />
                        <p className="font-medium">{category.name}</p>
                        <p className="text-xs text-muted-foreground">{category.description}</p>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          )
        ) : (
          <div className="space-y-3 pb-24" data-testid="bookmarks-list">
            {bookmarks.length === 0 ? (
              <div className="text-center py-12">
                <Bookmark className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No saved items yet</p>
                <p className="text-sm text-muted-foreground">Save verses and messages to find them here</p>
              </div>
            ) : (
              bookmarks.map((bookmark) => (
                <Card key={bookmark.id} className="rounded-2xl border-border/40">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">
                          {bookmark.type}
                        </span>
                        <p className="text-foreground leading-relaxed mt-1">
                          {bookmark.content}
                        </p>
                        {bookmark.reference && (
                          <p className="text-sm text-primary font-medium mt-2">{bookmark.reference}</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 flex-shrink-0"
                        onClick={() => removeBookmark(bookmark.id)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </ScrollArea>
        </>
      )}

      <BottomNav />
    </div>
  );
}
